/* eslint-disable @nx/enforce-module-boundaries */
import React, { Component, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import {
  EditorState,
  convertToRaw,
  ContentState,
  convertFromHTML,
  Modifier,
  CompositeDecorator,
} from 'draft-js';
import { Box, HStack, Stack, Spinner } from '@mybridge/ui';
import API from 'v4/store/common/api';

import draftToHtml from 'draftjs-to-html';

// Use react-draft-wysiwyg for toolbar support, but ensure decorators work
const Editor = dynamic(
  () => import('react-draft-wysiwyg').then((mod) => mod.Editor),
  { ssr: false }
);

if (typeof window !== 'undefined' && !window.__removeChildPatched) {
  window.__removeChildPatched = true;
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return newNode;
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

class EditorConvertToHTML extends Component {
  constructor(props) {
    super(props);
    this.searchTimeoutRef = null; // Store timeout ID
    this.lastSelectionRef = null; // Store last selection state
    this.editorContainerRef = React.createRef();
    
    // Create decorator for hashtags only
    this.findWithRegex = (regex, contentBlock, callback) => {
      const text = contentBlock.getText();
      let matchArr;
      while ((matchArr = regex.exec(text)) !== null) {
        const start = matchArr.index + (matchArr[1] ? matchArr[1].length : 0);
        const end = start + matchArr[2].length;
        callback(start, end);
      }
    };

    this.hashtagStrategy = (contentBlock, callback, contentState) => {
      this.findWithRegex(/(^|\s)(#[^\s@#]+)/g, contentBlock, callback);
    };

    // Create proper React component for hashtag decorator
    this.HashtagSpan = (props) => {
      return (
        <span 
          className="hashtag-highlight"
          style={{ color: '#488FEC', fontWeight: 600 }}
        >
          {props.children}
        </span>
      );
    };

    this.hashtagDecorator = new CompositeDecorator([
      { strategy: this.hashtagStrategy.bind(this), component: this.HashtagSpan },
    ]);

    this.decorateState = (state) => {
      // Use EditorState.set to properly apply decorator to editor state
      return EditorState.set(state, {
        decorator: this.hashtagDecorator,
      });
    };

    const initialEditorState = this.decorateState(EditorState.createEmpty(this.hashtagDecorator));

    this.state = {
      editorState: initialEditorState,
      toolbarOptions: this.props.toolbarOptions,
      toolbardropDown: this.props.toolbardropDown,
      toolbarItemOptions: this.props.toolbarItemOptions,
      hideToolbar: this.props.hideToolbar,
      placeholder: this.props.placeholder,
      // Hashtags states
      activeTrigger: null,
      suggestionQuery: '',
      showSuggestionList: false,
      apiHashtags: [],
      apiHashtagsFetching: false,
      suggestionPosition: { top: 0, left: 0 },
    };
  }
  componentDidMount() {
    if (this.props.defaultValue) {
      const contentState = ContentState.createFromBlockArray(
        convertFromHTML(this.props.defaultValue)
      );
      this.setState({
        editorState: this.decorateState(
          EditorState.createWithContent(contentState, this.hashtagDecorator)
        ),
      });
    }
    
    // Add keyboard event listeners
    this.setupEventListeners();
    
    // Add click outside handler
    this.handleClickOutside = (event) => {
      if (this.editorContainerRef.current && !this.editorContainerRef.current.contains(event.target)) {
        this.resetSuggestions();
      }
    };
    document.addEventListener('mousedown', this.handleClickOutside);
    
  }

  // Save cursor position before DOM manipulation
  saveCursorPosition = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const editorElement = this.editorContainerRef.current?.querySelector('.public-DraftEditor-content') ||
                         this.editorContainerRef.current?.querySelector('[contenteditable="true"]');
    
    if (!editorElement || !editorElement.contains(range.commonAncestorContainer)) {
      return null;
    }

    // Calculate character offset from the start of the editor
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editorElement);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    
    // Get text content up to cursor (excluding HTML tags)
    const textUpToCursor = preCaretRange.toString();
    const offset = textUpToCursor.length;

    return {
      offset,
      container: range.endContainer,
      offsetInNode: range.endOffset
    };
  };

  // Restore cursor position after DOM manipulation
  restoreCursorPosition = (savedPosition) => {
    if (!savedPosition) return;

    const editorElement = this.editorContainerRef.current?.querySelector('.public-DraftEditor-content') ||
                         this.editorContainerRef.current?.querySelector('[contenteditable="true"]');
    
    if (!editorElement) return;

    try {
      const selection = window.getSelection();
      const range = document.createRange();
      
      // Walk through all text nodes to find the position
      let currentOffset = 0;
      const walker = document.createTreeWalker(
        editorElement,
        NodeFilter.SHOW_TEXT,
        null
      );

      let targetNode = null;
      let targetOffset = 0;

      while (walker.nextNode()) {
        const node = walker.currentNode;
        const nodeLength = node.textContent.length;
        
        if (currentOffset + nodeLength >= savedPosition.offset) {
          // Found the node containing the cursor position
          targetNode = node;
          targetOffset = savedPosition.offset - currentOffset;
          break;
        }
        
        currentOffset += nodeLength;
      }

      // If we didn't find a node, place cursor at the end
      if (!targetNode) {
        // Find the last text node
        const allTextNodes = [];
        const textWalker = document.createTreeWalker(
          editorElement,
          NodeFilter.SHOW_TEXT,
          null
        );
        let textNode;
        while ((textNode = textWalker.nextNode())) {
          allTextNodes.push(textNode);
        }
        
        if (allTextNodes.length > 0) {
          targetNode = allTextNodes[allTextNodes.length - 1];
          targetOffset = targetNode.textContent.length;
        }
      }

      if (targetNode) {
        // Clamp offset to valid range
        const maxOffset = Math.min(targetOffset, targetNode.textContent.length);
        range.setStart(targetNode, maxOffset);
        range.setEnd(targetNode, maxOffset);
        
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Also update Draft.js selection if possible
        if (this.state.editorState) {
          // Trigger a focus event to ensure Draft.js knows about the selection
          editorElement.focus();
        }
      }
    } catch (error) {
      // Silently fail - cursor position restoration is best effort
    }
  };


  componentWillUnmount() {
    // Cleanup event listeners
    if (this.searchTimeoutRef) {
      clearTimeout(this.searchTimeoutRef);
    }
    this.removeEventListeners();
    if (this.handleClickOutside) {
      document.removeEventListener('mousedown', this.handleClickOutside);
    }
  }
  UNSAFE_componentWillReceiveProps(nextprops) {
    if (
      this.props.defaultValue !== nextprops.defaultValue &&
      nextprops.defaultValue !== this.lastHTML
    ) {
      if (nextprops.defaultValue) {
        const contentState = ContentState.createFromBlockArray(
          convertFromHTML(nextprops.defaultValue)
        );
        const editorState = this.decorateState(
          EditorState.createWithContent(contentState, this.hashtagDecorator)
        );
        this.lastHTML = nextprops.defaultValue;
        this.setState({
          editorState,
        });
      } else {
        const editorState = this.decorateState(EditorState.createEmpty(this.hashtagDecorator));
        this.lastHTML = '';
        this.setState({
          editorState,
        });
      }
    }
  }
  getSuggestionLabel = (option) => {
    if (!option) return '';
    const tag = option.tag || option.name || option.label || option.title || '';
    return tag.startsWith('#') ? tag : `#${tag}`;
  };

  resetSuggestions = () => {
    this.setState({
      showSuggestionList: false,
      activeTrigger: null,
      suggestionQuery: '',
      apiHashtags: [],
    });
    if (this.searchTimeoutRef) {
      clearTimeout(this.searchTimeoutRef);
      this.searchTimeoutRef = null;
    }
  };

  fetchHashtagsFromAPI = async (query) => {
    if (!query || query.trim().length === 0) {
      this.setState({ apiHashtags: [] });
      return;
    }

    this.setState({ apiHashtagsFetching: true });

    try {
      const response = await API({
        url: `api/v1/search/hashtags/?q=${encodeURIComponent(query)}`,
        method: 'GET',
      });

      if (response?.status && response?.data && Array.isArray(response.data)) {
        const transformedHashtags = response.data.map((hashtag) => ({
          id: hashtag.id,
          tag: hashtag.tag,
          count: hashtag.count || 0,
        }));
        this.setState({ apiHashtags: transformedHashtags, apiHashtagsFetching: false });
      } else {
        this.setState({ apiHashtags: [], apiHashtagsFetching: false });
      }
    } catch (error) {
      console.error('Error fetching hashtags:', error);
      this.setState({ apiHashtags: [], apiHashtagsFetching: false });
    }
  };

  detectSuggestions = (contentState, selectionState) => {
    if (!selectionState?.getHasFocus?.()) {
      this.resetSuggestions();
      return;
    }

    const blockKey = selectionState.getAnchorKey();
    const block = contentState.getBlockForKey(blockKey);
    if (!block) {
      this.resetSuggestions();
      return;
    }

    const textUntilCursor = block
      .getText()
      ?.slice(0, selectionState.getAnchorOffset());
    const triggerMatch = /(^|\s)(#)([^\s@#]*)$/.exec(textUntilCursor);

    if (triggerMatch) {
      const query = triggerMatch[3] || '';
      
      // Calculate position for suggestion dropdown
      const editorElement = this.editorContainerRef.current?.querySelector('.rdw-editor-main') ||
                           this.editorContainerRef.current?.querySelector('[contenteditable="true"]');
      
      let position = { top: 0, left: 0 };
      if (editorElement) {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          const containerRect = editorElement.getBoundingClientRect();
          position = {
            top: rect.bottom - containerRect.top + 5,
            left: rect.left - containerRect.left,
          };
        }
      }

      this.setState({
        activeTrigger: 'hashtag',
        suggestionQuery: query,
        showSuggestionList: true,
        suggestionPosition: position,
      });

      if (this.searchTimeoutRef) {
        clearTimeout(this.searchTimeoutRef);
      }
      this.searchTimeoutRef = setTimeout(() => {
        this.fetchHashtagsFromAPI(query);
      }, 300);
    } else {
      this.resetSuggestions();
    }
  };

  insertHashtagInEditor = (item) => {
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    const selectionState = this.lastSelectionRef || editorState.getSelection();
    const blockKey = selectionState.getAnchorKey();
    const block = contentState.getBlockForKey(blockKey);
    if (!block) return;

    const triggerChar = '#';
    const anchorOffset = selectionState.getAnchorOffset();
    const blockText = block.getText();

    const textUntilCursor = blockText.slice(0, anchorOffset);
    const triggerMatch = /(^|\s)(#)([^\s@#]*)$/.exec(textUntilCursor);
    const triggerIndex = triggerMatch
      ? triggerMatch.index + (triggerMatch[1]?.length || 0)
      : blockText.lastIndexOf(triggerChar, Math.max(anchorOffset - 1, 0));
    if (triggerIndex === -1) return;
    
    const typedChunkLength =
      (triggerMatch?.[2]?.length || 1) + (triggerMatch?.[3]?.length || 0);
    const replaceEnd =
      typedChunkLength > 0
        ? triggerIndex + typedChunkLength
        : Math.max(anchorOffset, triggerIndex + 1);

    let label = this.getSuggestionLabel(item);
    if (label.startsWith('#')) {
      label = label.substring(1);
    }
    const replacementText = `${triggerChar}${label} `;
    
    const replaceSelection = selectionState.merge({
      anchorOffset: triggerIndex,
      focusOffset: Math.max(replaceEnd, anchorOffset),
    });

    const newContentState = Modifier.replaceText(
      contentState,
      replaceSelection,
      replacementText
    );
    // Create new editor state with the replaced text
    const pushedState = EditorState.push(
      editorState,
      newContentState,
      'insert-characters'
    );
    const nextSelection = replaceSelection.merge({
      anchorOffset: triggerIndex + replacementText.length,
      focusOffset: triggerIndex + replacementText.length,
      hasFocus: true,
    });
    const withSelection = EditorState.forceSelection(
      pushedState,
      nextSelection
    );

    // Apply decorator to ensure hashtags are highlighted
    const decoratedState = this.decorateState(withSelection);

    this.lastSelectionRef = nextSelection;

    this.setState({ editorState: decoratedState });
    this.props.setEditorData(
      draftToHtml(convertToRaw(newContentState))
    );
    this.resetSuggestions();
  };

  handleSuggestionSelect = (item) => {
    if (!item) return;
    this.insertHashtagInEditor(item);
    this.props.onHashtagSelect?.(item);
  };

  setupEventListeners = () => {
    // Event listeners will be set up after editor renders
    setTimeout(() => {
      const editorElement = this.editorContainerRef.current?.querySelector('.rdw-editor-main') ||
                           this.editorContainerRef.current?.querySelector('[contenteditable="true"]');
      if (editorElement) {
        this.handleKeyUp = (e) => {
          if (e.key === 'Escape') {
            this.resetSuggestions();
          }
        };
        this.handleClick = () => {
          setTimeout(() => {
            const { editorState } = this.state;
            this.detectSuggestions(editorState.getCurrentContent(), editorState.getSelection());
          }, 0);
        };
        editorElement.addEventListener('keyup', this.handleKeyUp);
        editorElement.addEventListener('click', this.handleClick);
      }
    }, 100);
  };

  removeEventListeners = () => {
    const editorElement = this.editorContainerRef.current?.querySelector('.rdw-editor-main') ||
                         this.editorContainerRef.current?.querySelector('[contenteditable="true"]');
    if (editorElement) {
      if (this.handleKeyUp) {
        editorElement.removeEventListener('keyup', this.handleKeyUp);
      }
      if (this.handleClick) {
        editorElement.removeEventListener('click', this.handleClick);
      }
    }
  };

  onEditorStateChange = (editorState) => {
    // Native Draft.js Editor - decorator persists automatically
    // const decoratedState = this.decorateState(editorState);
    
    // Keep track of selection for suggestions
    this.lastSelectionRef = editorState.getSelection();
    
    const htmlContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    this.lastHTML = htmlContent;

    this.setState({
      editorState: editorState,
    });
    
    this.props.setEditorData(htmlContent);

    // Detect suggestions on change
    setTimeout(() => {
      this.detectSuggestions(
        editorState.getCurrentContent(),
        editorState.getSelection()
      );
    }, 0);
  };


  getFilteredSuggestions = () => {
    const { suggestionQuery, apiHashtags } = this.state;
    const query = suggestionQuery?.toLowerCase?.() || '';
    
    return apiHashtags
      .filter((option) => {
        const label = this.getSuggestionLabel(option)?.toLowerCase?.() || '';
        return !query || label.includes(query);
      })
      .slice(0, 8);
  };

  render() {
    const {
      editorState,
      toolbarOptions,
      toolbarItemOptions,
      toolbardropDown,
      hideToolbar,
      placeholder,
      showSuggestionList,
      suggestionPosition,
      apiHashtagsFetching,
    } = this.state;
    const filteredSuggestions = this.getFilteredSuggestions();
    return (
      <Box {...this.props} position="relative" ref={this.editorContainerRef}>
        <Editor
          editorState={editorState}
          wrapperClassName="demo-wrapper"
          editorClassName={
            this.props.editorClass ? this.props.editorClass : 'demo-editor'
          }
          onEditorStateChange={this.onEditorStateChange}
          toolbarHidden={hideToolbar}
          handlePastedText={() => false}
          placeholder={placeholder}
          // Use built-in prop for hashtag highlighting
          hashtag={{
            separator: ' ',
            trigger: '#',
          }}
          toolbar={{
            options: toolbarOptions,
            inline: {
              inDropdown: toolbardropDown,
              ...(toolbarItemOptions?.inline ?? {}),
            },
            list: {
              inDropdown: toolbardropDown,
              ...(toolbarItemOptions?.list ?? {}),
            },
            // need to uncomment this in future
            // textAlign: {
            //   inDropdown: toolbardropDown,
            //   ...(toolbarItemOptions?.textAlign ?? {}),
            // },
            // link: {
            //   inDropdown: toolbardropDown,
            //   ...(toolbarItemOptions?.link ?? {}),
            // },

            // history: {
            //   inDropdown: toolbardropDown,
            //   ...(toolbarItemOptions?.history ?? {}),
            // },
          }}
        />
        {showSuggestionList && (
          <Box
            position="absolute"
            top={`${suggestionPosition.top}px`}
            left={`${suggestionPosition.left}px`}
            mt={2}
            border="1px solid #EFEFEF"
            borderRadius="12px"
            bg="white"
            maxH="220px"
            overflowY="auto"
            boxShadow="md"
            zIndex={1000}
            minW="250px"
          >
            {filteredSuggestions.length ? (
              filteredSuggestions.map((item, idx) => (
                <HStack
                  key={`hashtag-suggestion-${idx}-${this.getSuggestionLabel(item)}`}
                  p={3}
                  spacing={3}
                  cursor="pointer"
                  _hover={{ backgroundColor: '#F8F8F8' }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    this.handleSuggestionSelect(item);
                  }}
                >
                  <Stack spacing={0}>
                    <Box fontWeight="600" color="#1D1D1F">
                      {this.getSuggestionLabel(item)}
                    </Box>
                    {item?.count ? (
                      <Box fontSize="12px" color="#5B5B5B">
                        {item.count} {item.count === 1 ? 'post' : 'posts'}
                      </Box>
                    ) : null}
                  </Stack>
                </HStack>
              ))
            ) : apiHashtagsFetching ? (
              <HStack p={3} justifyContent="center">
                <Spinner/>
              </HStack>
            ) : (
              <Box p={3} color="#5B5B5B">
                No hashtags found!
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  }
}

export default EditorConvertToHTML;

import { forwardRef } from 'react';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
export const NormalizedEditorConvertToHTML = forwardRef(
  ({ value, defaultValue, onChange, ...props }, ref) => {
    const [val, setVal] = useState(defaultValue);
    const direction = useDirection();
  
    
    useEffect(() => {
      if ((!val || val.length <= 0) && value?.length) {
        setVal(value);
      }
    }, [value, val]);
    return (
      <>
        <EditorConvertToHTML
          setEditorData={(e) => onChange?.(e)}
          defaultValue={val}
          direction={direction}
          {...props}
        />
      </>
    );
  }
);